import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Container,
  FloatingLabel,
  Form,
  Row,
} from 'react-bootstrap';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Header } from '../ui/Header';
import { Menu } from '../ui/Menu';
import { useTranslation } from 'react-i18next';
import { useLocalizedRoutes } from '../../hooks/useLocalizedRoutes';
import axiosApi from '../../axios/axios-api';
import { toast } from 'react-toastify';
import { LogoPetroprix } from '../ui/LogoPetroprix';
import { useDispatch, useSelector } from 'react-redux';
import Feedback from 'react-bootstrap/esm/Feedback';
import checkGovernmentIdentfier from '../../functions/checkGovernmentIdentfier';
import checkPostalCode from '../../functions/checkPostalCode';
import { MiniLoader } from '../ui/MiniLoader';
import { ValidateToken } from '../../helpers/validateToken';
import { startLogout } from '../../store/auth';
import { LoaderPetroprix } from '../ui/LoaderPetroprix';

const URL_GET_DATA_CLIENTE = 'factura/cuentaFacturacion';
const URL_POST_PUT_DATA_FACTURACION = 'factura/cuentaFacturacion';
const URL_GET_PROVINCIAS = 'factura/provincias';
const URL_GET_CODIGOS_PAISES = 'factura/codigosPaises';
const POSTAL_CODE_PATTERN = /^\d{2}-\d{3}$/;
const POLAND_COUNTRY_ID = '172';

const getPostalCodeResults = (postalCodeData) => {
  if (Array.isArray(postalCodeData)) {
    return postalCodeData;
  }

  if (Array.isArray(postalCodeData?.results)) {
    return postalCodeData.results;
  }

  if (Array.isArray(postalCodeData?.data)) {
    return postalCodeData.data;
  }

  if (Array.isArray(postalCodeData?.localities)) {
    return postalCodeData.localities;
  }

  if (Array.isArray(postalCodeData?.localidades)) {
    return postalCodeData.localidades;
  }

  return postalCodeData ? [postalCodeData] : [];
};

const buildLocalityOptionValue = (localityData) =>
  `${localityData?.id_poblacion || ''}::${localityData?.id_codigo_postal || ''}`;

const normaliseTreasuryAutofillText = (value) =>
  value === null || value === undefined ? '' : String(value).trim();

const normaliseTreasuryComparable = (value) =>
  normaliseTreasuryAutofillText(value).toLowerCase();

const getFirstTreasuryAutofillValue = (...values) => {
  for (const value of values) {
    const normalisedValue = normaliseTreasuryAutofillText(value);
    if (normalisedValue !== '') {
      return normalisedValue;
    }
  }

  return '';
};

const resolveTreasuryBillingInfoData = (validationData) => {
  if (!validationData || typeof validationData !== 'object') {
    return null;
  }

  const billingInfoFound =
    validationData.billingInfoFound === true ||
    validationData.billingInfoFound === 'true';

  if (
    !billingInfoFound ||
    !validationData.billingInfoData ||
    typeof validationData.billingInfoData !== 'object'
  ) {
    return null;
  }

  return validationData.billingInfoData;
};

const normaliseTreasuryAccountType = (accountType) => {
  const normalisedType = normaliseTreasuryComparable(accountType);

  if (normalisedType === 'fisica' || normalisedType === 'física') {
    return 'Fisica';
  }

  if (normalisedType === 'empresa') {
    return 'Empresa';
  }

  return '';
};

const resolveCountryIdFromTreasuryData = (
  countryCodes,
  addressData,
  billingInfoData,
) => {
  const directCountryId = getFirstTreasuryAutofillValue(
    billingInfoData?.countryId,
    addressData?.countryId,
    addressData?.id_codigo_pais,
  );

  if (
    directCountryId !== '' &&
    countryCodes.some(
      (country) => String(country.id_codigo_pais) === String(directCountryId),
    )
  ) {
    return String(directCountryId);
  }

  if (directCountryId !== '') {
    return String(directCountryId);
  }

  const countryCode = normaliseTreasuryComparable(
    getFirstTreasuryAutofillValue(
      billingInfoData?.countryCode,
      addressData?.countryCode,
      addressData?.codigoPais,
    ),
  );

  if (countryCode !== '') {
    const matchingCountryByCode = countryCodes.find(
      (country) => normaliseTreasuryComparable(country.codigo) === countryCode,
    );

    if (matchingCountryByCode) {
      return String(matchingCountryByCode.id_codigo_pais);
    }
  }

  const countryName = normaliseTreasuryComparable(
    getFirstTreasuryAutofillValue(
      billingInfoData?.countryName,
      addressData?.countryName,
      addressData?.pais,
      addressData?.country,
    ),
  );

  if (countryName !== '') {
    const matchingCountryByName = countryCodes.find(
      (country) => normaliseTreasuryComparable(country.pais) === countryName,
    );

    if (matchingCountryByName) {
      return String(matchingCountryByName.id_codigo_pais);
    }
  }

  return POLAND_COUNTRY_ID;
};

export const FormDatosFacturacionScreen = ({ mode }) => {
  const [clientData, setClientData] = useState({
    billingAccountId: '',
    name: '',
    lastName: '',
    nip: '',
    address: '',
    city: '',
    accountType: '',
    townshipId: '',
    provinciaId: '',
    distritoId: '',
    postalCodeId: '',
    postalCode: '',
    locality: '',
    countryId: POLAND_COUNTRY_ID,
    phoneNumber: '',
    period: '0',
  });

  const location = useLocation();
  const id = location.state?.id;

  const { token, nombre, apellido } = useSelector((state) => state.auth);

  const [loadingClientData, setLoadingClientData] = useState(false);
  const [loadingPoblaciones, setLoadingPoblaciones] = useState(false);
  const [loadingDistritos, setLoadingDistritos] = useState(false);
  const [loadingProvincias, setLoadingProvincias] = useState(false);
  const [validatingNIP, setValidatingNIP] = useState(false);
  const [validatingPostalCode, setValidatingPostalCode] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [editBlocked, setEditBlocked] = useState(false);
  const [poblaciones, setPoblaciones] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [pendingPostalCodeData, setPendingPostalCodeData] = useState(null);
  const [postalCodeLookupFailed, setPostalCodeLookupFailed] = useState(false);
  const [hasValidatedPostalCode, setHasValidatedPostalCode] = useState(false);
  const [nipValido, setNipValido] = useState(null);
  const [treasuryAutofillApplied, setTreasuryAutofillApplied] =
    useState(false);
  const [validated, setValidated] = useState(false);
  const [formErrors, setFormErrors] = useState({
    name: false,
    lastName: false,
    nip: false,
    address: false,
    city: false,
    accountType: false,
    postalCode: false,
    locality: false,
    townshipId: false,
    provinciaId: false,
    distritoId: false,
    countryId: false,
    phoneNumber: false,
  });
  const [codigosPaises, setCodigosPaises] = useState([]);
  const [loadingCodigosPaises, setLoadingCodigosPaises] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getPath } = useLocalizedRoutes();
  const isEditMode = mode === 'edit';
  const isReadOnlyEditField = isEditMode || editBlocked;
  const isPolandSelected =
    String(clientData.countryId || POLAND_COUNTRY_ID) === POLAND_COUNTRY_ID;
  const hasValidatedPostalCodeResults =
    isPolandSelected &&
    poblaciones.some((item) => String(item.id_codigo_postal || '') !== '');

  const fetchProvincias = useCallback(async () => {
    setLoadingProvincias(true);
    try {
      if (process.env.REACT_APP_URL_API && ValidateToken()) {
        const response = await axiosApi.get(URL_GET_PROVINCIAS, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        });
        if (response?.data?.code === 200) {
          setProvincias(response.data.data);
          setLoadingProvincias(false);
        } else {
          setLoadingProvincias(false);
        }
      } else {
        dispatch(startLogout('expired'));
        setLoadingProvincias(false);
      }
    } catch (error) {
      setLoadingProvincias(false);
    }
  }, []);

  useEffect(() => {
    fetchProvincias();
  }, [fetchProvincias]);

  const fetchClientData = useCallback(async () => {
    setLoadingClientData(true);
    try {
      if (process.env.REACT_APP_URL_API && ValidateToken()) {
        const response = await axiosApi.get(URL_GET_DATA_CLIENTE + '/' + id, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        });
        if (response?.data?.code === 200) {
          let data = response.data.data;
          setClientData((prevData) => ({
            ...prevData,
            ...data,
            city: data.city || data.locality || data.township || '',
            countryId: String(data.countryId || POLAND_COUNTRY_ID),
            period: String(data.period ?? '0'),
          }));
          if (data.postalCode || data.townshipId || data.locality) {
            setHasValidatedPostalCode(true);
          }
          if (data.nip !== '0' && data.nip !== '' && data.nip !== null) {
            setNipValido(true);
            setEditBlocked(true);
          } else if (String(data.countryId || POLAND_COUNTRY_ID) !== POLAND_COUNTRY_ID) {
            setNipValido(true);
          }
          setLoadingClientData(false);
        }
      } else {
        setLoadingClientData(false);
        dispatch(startLogout('expired'));
      }
    } catch (error) {
      setLoadingClientData(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'edit' && id) {
      fetchClientData();
    }
  }, [fetchClientData]);

  const clearLocationState = useCallback(
    ({
      preservePostalCode = true,
      preserveLocality = false,
      preserveCity = false,
    } = {}) => {
      setDistritos([]);
      setPoblaciones([]);
      setClientData((prevData) => ({
        ...prevData,
        provinciaId: '',
        distritoId: '',
        townshipId: '',
        postalCodeId: '',
        city: preserveCity ? prevData.city : '',
        locality: preserveLocality ? prevData.locality : '',
        postalCode: preservePostalCode ? prevData.postalCode : '',
      }));
    },
    [],
  );

  const clearTreasuryBillingAutofillData = useCallback(() => {
    if (!treasuryAutofillApplied) {
      return;
    }

    setPendingPostalCodeData(null);
    setPostalCodeLookupFailed(false);
    setHasValidatedPostalCode(false);
    setDistritos([]);
    setPoblaciones([]);
    setClientData((prevData) => ({
      ...prevData,
      countryId: POLAND_COUNTRY_ID,
      accountType: '',
      name: '',
      lastName: '',
      address: '',
      city: '',
      provinciaId: '',
      distritoId: '',
      townshipId: '',
      postalCodeId: '',
      postalCode: '',
      locality: '',
    }));
    setTreasuryAutofillApplied(false);
  }, [treasuryAutofillApplied]);

  const handleSelectPostalCodeLocality = useCallback(
    (
      selectedLocalityValue,
      availableLocalities = poblaciones,
      preferredCity = '',
    ) => {
      const selectedLocality = availableLocalities.find(
        (item) =>
          buildLocalityOptionValue(item) === String(selectedLocalityValue),
      );

      if (!selectedLocality) {
        return;
      }

      const provincia = provincias.find(
        (item) =>
          String(item.id_provincia) === String(selectedLocality.provinciaId),
      );

      setDistritos(provincia?.distritos || []);
      setClientData((prevData) => ({
        ...prevData,
        provinciaId: selectedLocality.provinciaId,
        distritoId: selectedLocality.distritoId,
        townshipId: String(selectedLocality.id_poblacion),
        postalCodeId: String(selectedLocality.id_codigo_postal),
        city:
          preferredCity ||
          selectedLocality.localidad ||
          selectedLocality.nombre_poblacion ||
          '',
        locality:
          selectedLocality.localidad || selectedLocality.nombre_poblacion || '',
      }));
      setPostalCodeLookupFailed(false);
    },
    [poblaciones, provincias],
  );

  const populateLocationFromTownshipId = useCallback(
    (
      townshipId,
      {
        locality = '',
        postalCodeId = '',
        city = '',
      } = {},
    ) => {
      if (!townshipId || provincias.length === 0) {
        return false;
      }

      for (const provincia of provincias) {
        for (const distrito of provincia.distritos) {
          const poblacionEncontrada = distrito.poblaciones.find(
            (poblacion) =>
              String(poblacion.id_poblacion) === String(townshipId),
          );

          if (!poblacionEncontrada) {
            continue;
          }

          const normalizedLocality =
            locality || poblacionEncontrada.nombre_poblacion || '';
          const normalizedPostalCodeId = String(postalCodeId || '');
          const resolvedLocalityOption = {
            ...poblacionEncontrada,
            provinciaId: String(provincia.id_provincia),
            distritoId: String(distrito.id_distrito),
            id_poblacion: String(poblacionEncontrada.id_poblacion),
            id_codigo_postal: normalizedPostalCodeId,
            localidad: normalizedLocality,
          };
          const districtLocalities = normalizedPostalCodeId
            ? [resolvedLocalityOption]
            : (distrito.poblaciones || []).map((poblacion) => ({
                ...poblacion,
                provinciaId: String(provincia.id_provincia),
                distritoId: String(distrito.id_distrito),
                id_poblacion: String(poblacion.id_poblacion),
                id_codigo_postal: '',
                localidad: poblacion.nombre_poblacion,
              }));

          setDistritos(provincia.distritos);
          setPoblaciones(districtLocalities);
          setClientData((prevData) => ({
            ...prevData,
            provinciaId: String(provincia.id_provincia),
            distritoId: String(distrito.id_distrito),
            townshipId: String(poblacionEncontrada.id_poblacion),
            postalCodeId: normalizedPostalCodeId,
            city: city || normalizedLocality,
            locality: normalizedLocality,
          }));
          return true;
        }
      }

      return false;
    },
    [provincias],
  );

  const populateLocationFromAddressData = useCallback(
    (addressData) => {
      if (!addressData || typeof addressData !== 'object') {
        return false;
      }

      const townshipId = getFirstTreasuryAutofillValue(
        addressData.townshipId,
        addressData.id_poblacion,
        addressData.idPoblacion,
      );
      const postalCodeId = getFirstTreasuryAutofillValue(
        addressData.postalCodeId,
        addressData.id_codigo_postal,
        addressData.idCodigoPostal,
      );
      const locality = getFirstTreasuryAutofillValue(
        addressData.township,
        addressData.locality,
        addressData.localidad,
        addressData.city,
        addressData.poblacion,
      );
      const city = getFirstTreasuryAutofillValue(
        addressData.city,
        addressData.locality,
        addressData.localidad,
        addressData.township,
        addressData.poblacion,
      );
      const districtName = getFirstTreasuryAutofillValue(
        addressData.district,
        addressData.county,
        addressData.distrito,
      );
      const provinceName = getFirstTreasuryAutofillValue(
        addressData.province,
        addressData.state,
        addressData.voivodeship,
        addressData.region,
        addressData.provincia,
      );

      if (
        townshipId &&
        populateLocationFromTownshipId(townshipId, {
          locality,
          postalCodeId,
          city,
        })
      ) {
        return true;
      }

      if (!locality || provincias.length === 0) {
        return false;
      }

      for (const provincia of provincias) {
        if (
          provinceName &&
          normaliseTreasuryComparable(provincia.nombre_provincia) !==
            normaliseTreasuryComparable(provinceName)
        ) {
          continue;
        }

        for (const distrito of provincia.distritos) {
          if (
            districtName &&
            normaliseTreasuryComparable(distrito.nombre_distrito) !==
              normaliseTreasuryComparable(districtName)
          ) {
            continue;
          }

          const poblacionEncontrada = distrito.poblaciones.find(
            (poblacion) =>
              normaliseTreasuryComparable(poblacion.nombre_poblacion) ===
              normaliseTreasuryComparable(locality),
          );

          if (!poblacionEncontrada) {
            continue;
          }

          return populateLocationFromTownshipId(poblacionEncontrada.id_poblacion, {
            locality,
            postalCodeId,
            city,
          });
        }
      }

      return false;
    },
    [populateLocationFromTownshipId, provincias],
  );

  const applyPostalCodeData = useCallback(
    (postalCodeData, preferredLocality = '', preferredCity = '') => {
      if (!postalCodeData || provincias.length === 0) {
        return false;
      }

      const normalizedResults = getPostalCodeResults(postalCodeData)
        .map((item) => {
          const provincia = provincias.find(
            (provinciaItem) =>
              Number(provinciaItem.codigo_provincia) ===
              Number(item.codigo_provincia),
          );
          const distrito = provincia?.distritos.find(
            (distritoItem) =>
              Number(distritoItem.codigo_distrito) ===
              Number(item.codigo_distrito),
          );
          const poblacion = distrito?.poblaciones.find(
            (poblacionItem) =>
              Number(poblacionItem.id_poblacion) === Number(item.id_poblacion),
          );

          if (!provincia || !distrito || !poblacion) {
            return null;
          }

          return {
            ...poblacion,
            provinciaId: String(provincia.id_provincia),
            distritoId: String(distrito.id_distrito),
            id_poblacion: String(item.id_poblacion),
            id_codigo_postal: String(item.id_codigo_postal),
            codigo_postal: item.codigo_postal,
            localidad: item.localidad || poblacion.nombre_poblacion,
          };
        })
        .filter(Boolean)
        .filter(
          (item, index, array) =>
            array.findIndex(
              (candidate) =>
                String(candidate.id_poblacion) === String(item.id_poblacion) &&
                String(candidate.id_codigo_postal) ===
                  String(item.id_codigo_postal),
            ) === index,
        );

      if (normalizedResults.length === 0) {
        return false;
      }

      setPoblaciones(normalizedResults);
      const preferredLocalityComparable =
        normaliseTreasuryComparable(preferredLocality);
      const selectedLocality =
        normalizedResults.find(
          (item) =>
            normaliseTreasuryComparable(
              item.localidad || item.nombre_poblacion,
            ) === preferredLocalityComparable,
        ) || normalizedResults[0];

      handleSelectPostalCodeLocality(
        buildLocalityOptionValue(selectedLocality),
        normalizedResults,
        preferredCity,
      );

      return true;
    },
    [handleSelectPostalCodeLocality, provincias],
  );

  useEffect(() => {
    if (!pendingPostalCodeData) {
      return;
    }

    if (provincias.length === 0) {
      return;
    }

    const {
      postalCodeData,
      preferredLocality = '',
      preferredCity = '',
      addressData = null,
      preserveLocality = true,
    } = pendingPostalCodeData;

    const postalCodeApplied =
      postalCodeData &&
      applyPostalCodeData(postalCodeData, preferredLocality, preferredCity);

    if (!postalCodeApplied && !populateLocationFromAddressData(addressData)) {
      clearLocationState({ preserveLocality, preserveCity: true });
      setPostalCodeLookupFailed(true);
    } else {
      setPostalCodeLookupFailed(false);
    }

    setPendingPostalCodeData(null);
  }, [
    applyPostalCodeData,
    clearLocationState,
    pendingPostalCodeData,
    populateLocationFromAddressData,
    provincias.length,
  ]);

  const applyTreasuryBillingInfo = useCallback(
    async (validationData) => {
      const billingInfoData = resolveTreasuryBillingInfoData(validationData);

      if (!billingInfoData) {
        clearTreasuryBillingAutofillData();
        return false;
      }

      const addressData =
        billingInfoData.address && typeof billingInfoData.address === 'object'
          ? billingInfoData.address
          : null;
      const resolvedCountryId = resolveCountryIdFromTreasuryData(
        codigosPaises,
        addressData,
        billingInfoData,
      );
      const resolvedAccountType = normaliseTreasuryAccountType(
        getFirstTreasuryAutofillValue(
          billingInfoData.type,
          billingInfoData.accountType,
          billingInfoData.customerType,
        ),
      );
      const billingName = getFirstTreasuryAutofillValue(
        billingInfoData.name,
        billingInfoData.entityName,
      );
      const billingLastName = getFirstTreasuryAutofillValue(
        billingInfoData.lastName,
        billingInfoData.surname,
        billingInfoData.secondName,
      );
      const billingAddress = getFirstTreasuryAutofillValue(
        addressData?.street,
        addressData?.street1,
        addressData?.addressLine1,
        addressData?.line1,
        addressData?.road,
        typeof billingInfoData.address === 'string'
          ? billingInfoData.address
          : '',
      );
      const billingPostalCode = getFirstTreasuryAutofillValue(
        addressData?.postalCode,
        addressData?.codigoPostal,
        addressData?.zipCode,
        addressData?.zip,
        billingInfoData.postalCode,
      );
      const billingLocality = getFirstTreasuryAutofillValue(
        addressData?.locality,
        addressData?.localidad,
        addressData?.city,
        addressData?.township,
        billingInfoData.locality,
      );
      const billingCity = getFirstTreasuryAutofillValue(
        addressData?.city,
        addressData?.locality,
        addressData?.localidad,
        addressData?.township,
        billingInfoData.city,
        billingInfoData.locality,
      );

      setPendingPostalCodeData(null);
      setPostalCodeLookupFailed(false);
      setClientData((prevData) => ({
        ...prevData,
        countryId: resolvedCountryId,
        accountType: resolvedAccountType || prevData.accountType,
        name: billingName || prevData.name,
        lastName:
          resolvedAccountType === 'Empresa'
            ? ''
            : billingLastName || prevData.lastName,
        address: billingAddress || prevData.address,
        city: billingCity || prevData.city,
        provinciaId: '',
        distritoId: '',
        townshipId: '',
        postalCodeId: '',
        postalCode: billingPostalCode || '',
        locality: billingLocality || '',
      }));

      if (String(resolvedCountryId) !== POLAND_COUNTRY_ID) {
        setDistritos([]);
        setPoblaciones([]);
        setHasValidatedPostalCode(
          billingPostalCode.trim() !== '' || billingLocality.trim() !== '',
        );
        setTreasuryAutofillApplied(true);
        return true;
      }

      if (billingPostalCode.trim() === '') {
        const locationResolved = populateLocationFromAddressData(addressData);
        setHasValidatedPostalCode(
          billingLocality.trim() !== '' || billingCity.trim() !== '',
        );
        setTreasuryAutofillApplied(true);
        return true;
      }

      const normalizedPostalCode = billingPostalCode.trim();
      const postalCodeResponse = await checkPostalCode(
        token,
        normalizedPostalCode,
      );

      setHasValidatedPostalCode(true);
      setPendingPostalCodeData({
        postalCodeData:
          postalCodeResponse.code === 200 ? postalCodeResponse.data : null,
        preferredLocality: billingLocality,
        preferredCity: billingCity,
        addressData,
        preserveLocality: true,
      });

      setTreasuryAutofillApplied(true);
      return true;
    },
    [
      clearTreasuryBillingAutofillData,
      codigosPaises,
      populateLocationFromAddressData,
      token,
    ],
  );

  const handleSave = async (e) => {
    e.preventDefault();
    setSavingData(true);

    const validateForm = {
      name: false,
      nip: false,
      address: false,
      city: false,
      accountType: false,
      postalCode: false,
      locality: false,
      townshipId: false,
      countryId: false,
    };

    // Validación de datos
    validateForm.name = clientData.name !== '' && clientData.name.length >= 3;
    validateForm.accountType = clientData.accountType !== '';
    validateForm.address = clientData.address !== '';
    validateForm.city = clientData.city.trim() !== '';
    validateForm.postalCode = clientData.postalCode.trim() !== '';
    validateForm.townshipId = true;
    validateForm.locality = true;
    validateForm.countryId = clientData.countryId !== '';
    validateForm.nip = clientData.nip !== '';

    // Validación del NIP solo para Polonia
    if (isPolandSelected && clientData.nip !== '') {
      const nipValidation = await checkGovernmentIdentfier(
        token,
        clientData.nip,
      );
      validateForm.nip = nipValidation.code === 200;
    }

    setFormErrors(validateForm);
    setValidated(true); // Activamos la validación del formulario

    // Verificar si hay errores
    if (Object.values(validateForm).some((value) => value === false)) {
      const campoInvalido = Object.keys(validateForm).find(
        (key) => validateForm[key] === false,
      );
      setSavingData(false);
      return;
    }

    let method = 'POST';
    let endpoint = URL_POST_PUT_DATA_FACTURACION;

    if (mode === 'edit') {
      method = 'PUT';
      endpoint =
        URL_POST_PUT_DATA_FACTURACION + '/' + clientData.billingAccountId;
    }

    const dataSend = new FormData();
    const appendIfPresent = (fieldName, value) => {
      if (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ''
      ) {
        dataSend.append(fieldName, value);
      }
    };

    if (mode === 'edit') {
      appendIfPresent('name', clientData.name);
      appendIfPresent('lastName', clientData.lastName);
      appendIfPresent('nip', clientData.nip);
      appendIfPresent('accountType', clientData.accountType);
      appendIfPresent('address', clientData.address);
      if (isPolandSelected) {
        appendIfPresent('townshipId', clientData.townshipId);
        appendIfPresent('postalCodeId', clientData.postalCodeId);
      }
      appendIfPresent('postalCode', clientData.postalCode);
      appendIfPresent('locality', clientData.locality);
      appendIfPresent('city', clientData.city);
      appendIfPresent('countryId', clientData.countryId);
      appendIfPresent('phoneNumber', clientData.phoneNumber);
      appendIfPresent('period', clientData.period);
    } else {
      dataSend.append('name', clientData.name);
      dataSend.append('lastName', clientData.lastName);
      dataSend.append('nip', clientData.nip);
      dataSend.append('accountType', clientData.accountType);
      dataSend.append('address', clientData.address || '');
      if (
        isPolandSelected &&
        clientData.townshipId !== null &&
        clientData.townshipId !== ''
      ) {
        dataSend.append('townshipId', clientData.townshipId);
        if (
          clientData.postalCodeId !== null &&
          clientData.postalCodeId !== ''
        ) {
          dataSend.append('postalCodeId', clientData.postalCodeId);
        }
      }
      dataSend.append('postalCode', clientData.postalCode || '');
      dataSend.append('locality', clientData.locality || '');
      dataSend.append('city', clientData.city || '');
      dataSend.append('countryId', clientData.countryId);
      dataSend.append('phoneNumber', clientData.phoneNumber);
      dataSend.append('period', clientData.period);
    }

    if (process.env.REACT_APP_URL_API && ValidateToken()) {
      await axiosApi[method.toLowerCase()](endpoint, dataSend, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      })
        .then((response) => {
          if (response?.data?.code === 200) {
            toast(t('datos.facturacion.form.result-ok'), {
              icon: <LogoPetroprix />,
            });
            if (ValidateToken()) {
              setSavingData(false);
              navigate(getPath('billingData'), {
                state: {
                  editedFacturacion: true,
                },
              });
            } else {
              setSavingData(false);
              dispatch(startLogout('expired'));
            }
          } else {
            setSavingData(false);
            toast(response.data.message, {
              icon: <LogoPetroprix />,
            });
          }
        })
        .catch(() => null)
        .finally(() => setSavingData(false));
    } else {
      setSavingData(false);
      dispatch(startLogout('expired'));
    }
  };

  const handleChangePostalCode = (e) => {
    const postalCode = e.target.value;

    setPendingPostalCodeData(null);
    setPostalCodeLookupFailed(false);
    setHasValidatedPostalCode(false);
    setDistritos([]);
    setPoblaciones([]);
    setClientData((prevData) => ({
      ...prevData,
      postalCode,
      postalCodeId: '',
      provinciaId: '',
      distritoId: '',
      townshipId: '',
      locality: '',
      city: prevData.city,
    }));
  };

  const handleValidatePostalCode = async (e) => {
    const postalCode = e.target.value.trim();

    setClientData((prevData) => ({
      ...prevData,
      postalCode,
    }));

    if (!isPolandSelected) {
      setHasValidatedPostalCode(postalCode !== '');
      setPendingPostalCodeData(null);
      setPostalCodeLookupFailed(false);
      clearLocationState({ preserveLocality: true, preserveCity: true });
      return;
    }

    if (postalCode === '') {
      setHasValidatedPostalCode(false);
      setPendingPostalCodeData(null);
      setPostalCodeLookupFailed(false);
      clearLocationState({ preserveCity: true });
      return;
    }

    if (!POSTAL_CODE_PATTERN.test(postalCode)) {
      setHasValidatedPostalCode(true);
      setPendingPostalCodeData(null);
      setPostalCodeLookupFailed(true);
      clearLocationState({ preserveCity: true });
      return;
    }

    setValidatingPostalCode(true);
    const response = await checkPostalCode(token, postalCode);
    setValidatingPostalCode(false);

    if (response.code !== 200 || !response.data) {
      setHasValidatedPostalCode(true);
      setPendingPostalCodeData(null);
      setPostalCodeLookupFailed(true);
      clearLocationState({ preserveCity: true });
      return;
    }

    setHasValidatedPostalCode(true);
    setPendingPostalCodeData({
      postalCodeData: response.data,
      preferredLocality: clientData.locality,
      preferredCity: clientData.city,
      addressData: null,
      preserveLocality: true,
    });
  };

  const handleChangeCountry = (e) => {
    const countryId = e.target.value;
    const countryIsPoland = countryId === POLAND_COUNTRY_ID;

    setClientData((prevData) => ({
      ...prevData,
      countryId,
      provinciaId: '',
      distritoId: '',
      townshipId: '',
      postalCodeId: '',
      postalCode: '',
      locality: '',
      city: '',
    }));
    setPendingPostalCodeData(null);
    setPostalCodeLookupFailed(false);
    setHasValidatedPostalCode(false);
    setDistritos([]);
    setPoblaciones([]);
    setNipValido(countryIsPoland ? null : true);
  };

  const handleChangeProvincia = (e) => {
    setLoadingDistritos(true);
    const provincia = provincias.find(
      (item) => String(item.id_provincia) === String(e.target.value),
    );

    if (!provincia) {
      setLoadingDistritos(false);
      return;
    }

    setClientData((prevData) => ({
      ...prevData,
      provinciaId: String(provincia.id_provincia),
      distritoId: '',
      townshipId: '',
      postalCodeId: '',
      locality: '',
      city: '',
    }));
    setDistritos(provincia.distritos || []);
    setPoblaciones([]);
    setLoadingDistritos(false);
  };

  const handleChangeDistrito = (e) => {
    setLoadingPoblaciones(true);
    const distrito = distritos.find(
      (item) => String(item.id_distrito) === String(e.target.value),
    );

    if (!distrito) {
      setLoadingPoblaciones(false);
      return;
    }

    setClientData((prevData) => ({
      ...prevData,
      distritoId: String(distrito.id_distrito),
      townshipId: '',
      postalCodeId: '',
      locality: '',
      city: '',
    }));
    setPoblaciones(
      (distrito.poblaciones || []).map((poblacion) => ({
        ...poblacion,
        provinciaId: clientData.provinciaId,
        distritoId: String(distrito.id_distrito),
        id_poblacion: String(poblacion.id_poblacion),
        id_codigo_postal: '',
        localidad: poblacion.nombre_poblacion,
      })),
    );
    setLoadingPoblaciones(false);
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  //Efecto para cuando se carguen las provincias si el cliente ya tiene una población, rellenamos el distrituo y pronvicia del cliente
  // y seleccionamos la población
  useEffect(() => {
    setLoadingPoblaciones(false);
    if (clientData.townshipId !== '' && provincias.length > 0) {
      for (const provincia of provincias) {
        for (const distrito of provincia.distritos) {
          const poblacionEncontrada = distrito.poblaciones.find(
            (c) => c.id_poblacion === parseInt(clientData.townshipId),
          );
          if (poblacionEncontrada) {
            setClientData((prevData) => ({
              ...prevData,
              provinciaId: provincia.id_provincia,
              distritoId: distrito.id_distrito,
            }));
            setDistritos(provincia.distritos);
            setPoblaciones((prevPoblaciones) =>
              prevPoblaciones.length > 1
                ? prevPoblaciones
                : [
                    {
                      ...poblacionEncontrada,
                      provinciaId: String(provincia.id_provincia),
                      distritoId: String(distrito.id_distrito),
                      id_poblacion: String(poblacionEncontrada.id_poblacion),
                      id_codigo_postal: String(clientData.postalCodeId || ''),
                      localidad:
                        clientData.locality ||
                        poblacionEncontrada.nombre_poblacion,
                    },
                  ],
            );
            return; // Salir de los bucles una vez encontrado
          }
        }
      }
    }
  }, [loadingClientData, clientData.townshipId, provincias.length]);

  const fetchCountryCodes = useCallback(async () => {
    setLoadingCodigosPaises(true);
    try {
      if (process.env.REACT_APP_URL_API && ValidateToken()) {
        const response = await axiosApi.get(URL_GET_CODIGOS_PAISES, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        });
        if (response?.data?.code === 200) {
          setCodigosPaises(response.data.data);
          setLoadingCodigosPaises(false);
        }
      } else {
        setLoadingCodigosPaises(false);
        dispatch(startLogout('expired'));
      }
    } catch (error) {
      setLoadingCodigosPaises(false);
    }
  }, []);

  useEffect(() => {
    fetchCountryCodes();
  }, []);

  return (
    <>
      <Container className='content-body mt-4 px-4'>
        <Container>
          <Row className='page-header pb-4'>
            <h1>
              {mode === 'edit'
                ? t('datos.facturacion.form.h1.edit')
                : t('datos.facturacion.form.h1.new')}
            </h1>
          </Row>
        </Container>
        <Container>
          <Row className='content-section mb-4 bg-white radius-8'>
            {loadingClientData && mode === 'edit' ? (
              <LoaderPetroprix />
            ) : (
              <Form
                className='datos-facturacion'
                autoComplete='off'
                noValidate
                validated={validated}
                onSubmit={(e) => handleSave(e)}
              >
                <Row>
                  <Col sm={12}>
                    <FloatingLabel
                      label={
                        loadingCodigosPaises
                          ? ''
                          : t('datos.facturacion.form.codigo-pais')
                      }
                      className='mb-3'
                    >
                      {loadingCodigosPaises ? (
                        <MiniLoader />
                      ) : (
                        <Form.Select
                          aria-label='Codigo de pais'
                          id='codigo-pais'
                          placeholder={t(
                            'datos.facturacion.form.codigo-pais.select',
                          )}
                          value={clientData?.countryId || POLAND_COUNTRY_ID}
                          disabled={isReadOnlyEditField}
                          onChange={(e) => handleChangeCountry(e)}
                          isInvalid={validated && !formErrors.countryId}
                          isValid={validated && formErrors.countryId}
                          required
                        >
                          <option value='' disabled>
                            {t('datos.facturacion.form.codigo-pais.select')}
                          </option>

                          {codigosPaises.length > 0 &&
                            codigosPaises.map((codigo) => (
                              <option
                                key={codigo.id_codigo_pais}
                                value={codigo.id_codigo_pais}
                              >
                                {codigo.pais} ({codigo.codigo})
                              </option>
                            ))}
                        </Form.Select>
                      )}
                      <Feedback type='invalid'>
                        {t('datos.facturacion.form.codigo-pais.invalid')}
                      </Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row>
                  <Col sm={12} lg={6}>
                    <FloatingLabel
                      label={t(
                        isPolandSelected
                          ? 'datos.facturacion.form.dni'
                          : 'datos.facturacion.form.vat',
                      )}
                      className='mb-3'
                    >
                      <Form.Control
                        type='text'
                        placeholder={t(
                          isPolandSelected
                            ? 'datos.facturacion.form.dni'
                            : 'datos.facturacion.form.vat',
                        )}
                        value={clientData?.nip !== '0' ? clientData?.nip : ''}
                        disabled={isReadOnlyEditField}
                        onChange={(e) => {
                          const nextNip = e.target.value;

                          if (
                            isPolandSelected &&
                            treasuryAutofillApplied &&
                            nextNip !== clientData.nip
                          ) {
                            clearTreasuryBillingAutofillData();
                          }

                          setClientData((prevData) => ({
                            ...prevData,
                            nip: nextNip,
                          }));

                          if (nextNip === '') {
                            setNipValido(null);
                          }
                        }}
                        onBlur={(e) => {
                          if (isPolandSelected && e.target.value !== '') {
                            setValidatingNIP(true);
                            const isValidForm = validated;
                            setValidated(false);
                            e.target.setCustomValidity(' ');
                            e.target.classList.remove('is-valid');
                            e.target.classList.remove('is-invalid');
                            setFormErrors({
                              ...formErrors,
                              nip: false,
                            });
                            checkGovernmentIdentfier(
                              token,
                              e.target.value,
                            ).then(async (validNIP) => {
                              setValidatingNIP(false);
                              if (validNIP.code !== 200) {
                                setNipValido(false);
                                clearTreasuryBillingAutofillData();
                                e.target.setCustomValidity(' ');
                                e.target.classList.remove('is-valid');
                                e.target.classList.add('is-invalid');
                                setFormErrors({
                                  ...formErrors,
                                  nip: false,
                                });
                              } else {
                                setNipValido(true);
                                await applyTreasuryBillingInfo(validNIP.data);
                                e.target.setCustomValidity('');
                                e.target.classList.remove('is-invalid');
                                e.target.classList.add('is-valid');
                                setFormErrors({
                                  ...formErrors,
                                  nip: true,
                                });
                              }
                              setValidated(isValidForm);
                            });
                          } else if (treasuryAutofillApplied) {
                            clearTreasuryBillingAutofillData();
                          }
                        }}
                        isInvalid={
                          !validatingNIP && validated && !formErrors.nip
                        }
                        isValid={!validatingNIP && validated && formErrors.nip}
                        required
                      />
                      {validatingNIP && (
                        <>
                          <div className='datos-form-validating'>
                            <MiniLoader />
                          </div>
                          <p className='datos-form-validating-text'>
                            {t('datos.facturacion.form.validating')}
                          </p>
                        </>
                      )}
                      {!validatingNIP && (
                        <Feedback type='invalid'>
                          {t(
                            isPolandSelected
                              ? 'datos.facturacion.form.dni.invalid'
                              : 'datos.facturacion.form.vat.invalid',
                          )}
                        </Feedback>
                      )}
                    </FloatingLabel>
                  </Col>

                  <Col sm={12} lg={6}>
                    <FloatingLabel
                      label={t('datos.facturacion.form.tipo-cuenta.select')}
                      className='mb-3'
                    >
                      <Form.Select
                        id='tipo-cuenta'
                        placeholder={t(
                          'datos.facturacion.form.tipo-cuenta.select',
                        )}
                        value={clientData?.accountType || ''}
                        isInvalid={validated && !formErrors.accountType}
                        isValid={validated && formErrors.accountType}
                        required
                        disabled={isReadOnlyEditField}
                        onChange={(e) => {
                          setClientData({
                            ...clientData,
                            accountType: e.target.value,
                          });
                        }}
                      >
                        <option value='' disabled>
                          {t('datos.facturacion.form.tipo-cuenta.select')}
                        </option>
                        <option value='Fisica'>
                          {t('datos.facturacion.form.tipo-cuenta.fisico')}
                        </option>
                        <option value='Empresa'>
                          {t('datos.facturacion.form.tipo-cuenta.empresa')}
                        </option>
                      </Form.Select>
                      <Feedback type='invalid'>
                        {t('datos.facturacion.form.tipo-cuenta.invalid')}
                      </Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row>
                  <Col sm={12} lg={6}>
                    <FloatingLabel
                      label={
                        clientData.accountType === '' ||
                        clientData.accountType === 'Fisica'
                          ? t('datos.facturacion.form.nombre')
                          : t('datos.facturacion.form.nombre-empresa')
                      }
                      className='mb-3'
                    >
                      <Form.Control
                        type='text'
                        placeholder={
                          clientData.accountType === '' ||
                          clientData.accountType === 'Fisica'
                            ? t('datos.facturacion.form.nombre')
                            : t('datos.facturacion.form.nombre-empresa')
                        }
                        value={clientData?.name}
                        disabled={isReadOnlyEditField}
                        onChange={(e) => {
                          setClientData({
                            ...clientData,
                            name: e.target.value,
                          });
                        }}
                        isInvalid={validated && !formErrors.name}
                        isValid={validated && formErrors.name}
                        required
                        pattern='.{3,}'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {t('datos.facturacion.form.nombre.invalid')}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Col>

                  {clientData.accountType === '' ||
                  clientData.accountType === 'Fisica' ? (
                    <Col sm={12} lg={6}>
                      <FloatingLabel
                        label={t('datos.facturacion.form.apellidos')}
                        className='mb-3'
                      >
                        <Form.Control
                          type='text'
                          placeholder={t('datos.facturacion.form.apellidos')}
                          value={clientData?.lastName}
                          disabled={isReadOnlyEditField}
                          onChange={(e) => {
                            setClientData({
                              ...clientData,
                              lastName: e.target.value,
                            });
                          }}
                        />
                        <Feedback type='invalid'>
                          {t('datos.facturacion.form.apellidos.invalid')}
                        </Feedback>
                      </FloatingLabel>
                    </Col>
                  ) : null}
                </Row>

                <Row>
                  <Col sm={12} lg={6}>
                    <FloatingLabel
                      label={t('datos.facturacion.form.periodo-facturacion')}
                      className='mb-3'
                    >
                      <Form.Select
                        value={String(clientData?.period || '0')}
                        onChange={(e) => {
                          setClientData({
                            ...clientData,
                            period: e.target.value,
                          });
                        }}
                      >
                        <option value='0'>
                          {t(
                            'datos.facturacion.form.periodo-facturacion.sin-periodo',
                          )}
                        </option>
                        {/* Reserved for a future backend enablement.
                        <option value='1'>
                          {t(
                            'datos.facturacion.form.periodo-facturacion.mensual',
                          )}
                        </option> */}
                        <option value='2'>
                          {t(
                            'datos.facturacion.form.periodo-facturacion.repostaje',
                          )}
                        </option>
                      </Form.Select>
                    </FloatingLabel>
                  </Col>

                  <Col sm={12} lg={6}>
                    <FloatingLabel
                      label={t('datos.facturacion.form.telefono')}
                      className='mb-3'
                    >
                      <Form.Control
                        type='text'
                        placeholder={t('datos.facturacion.form.telefono')}
                        value={clientData?.phoneNumber || ''}
                        autoComplete='datos-facturacion-telefono'
                        disabled={isEditMode}
                        onChange={(e) => {
                          setClientData({
                            ...clientData,
                            phoneNumber: e.target.value,
                          });
                        }}
                      />
                      <Feedback type='invalid'>
                        {t('datos.facturacion.form.telefono.invalid')}
                      </Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row>
                  <Col sm={12} lg={6}>
                    <FloatingLabel
                      label={t('datos.facturacion.form.codigo-postal')}
                      className='mb-3'
                    >
                      <Form.Control
                        type='text'
                        placeholder={t('datos.facturacion.form.codigo-postal')}
                        value={clientData?.postalCode || ''}
                        autoComplete='postal-code'
                        disabled={isEditMode}
                        onChange={(e) => handleChangePostalCode(e)}
                        onBlur={(e) => handleValidatePostalCode(e)}
                        isInvalid={
                          (isPolandSelected && postalCodeLookupFailed) ||
                          (validated && !formErrors.postalCode)
                        }
                        isValid={
                          !validatingPostalCode &&
                          !postalCodeLookupFailed &&
                          validated &&
                          formErrors.postalCode
                        }
                        required
                      />
                      {validatingPostalCode && (
                        <>
                          <div className='datos-form-validating'>
                            <MiniLoader />
                          </div>
                          <p className='datos-form-validating-text'>
                            {t('datos.facturacion.form.validating-postal-code')}
                          </p>
                        </>
                      )}
                      {!validatingPostalCode && (
                        <Feedback type='invalid'>
                          {t('datos.facturacion.form.codigo-postal.invalid')}
                        </Feedback>
                      )}
                    </FloatingLabel>
                  </Col>

                  <Col sm={12} lg={6}>
                    <FloatingLabel
                      label={t('datos.facturacion.form.direccion')}
                      className='mb-3'
                    >
                      <Form.Control
                        type='text'
                        placeholder={t('datos.facturacion.form.direccion')}
                        value={clientData?.address}
                        autoComplete='datos-facturacion-direccion'
                        disabled={isEditMode}
                        onChange={(e) => {
                          setClientData({
                            ...clientData,
                            address: e.target.value,
                          });
                        }}
                        isInvalid={validated && !formErrors.address}
                        isValid={validated && formErrors.address}
                      />
                      <Feedback type='invalid'>
                        {t('datos.facturacion.form.direccion.invalid')}
                      </Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row>
                  <Col sm={12}>
                    <FloatingLabel
                      label={t('datos.facturacion.form.ciudad')}
                      className='mb-3'
                    >
                      <Form.Control
                        type='text'
                        placeholder={t('datos.facturacion.form.ciudad')}
                        value={clientData?.city || ''}
                        autoComplete='address-level2'
                        disabled={isEditMode}
                        onChange={(e) => {
                          setClientData({
                            ...clientData,
                            city: e.target.value,
                          });
                        }}
                        isInvalid={validated && !formErrors.city}
                        isValid={validated && formErrors.city}
                        required
                      />
                      <Feedback type='invalid'>
                        {t('datos.facturacion.form.ciudad.invalid')}
                      </Feedback>
                    </FloatingLabel>
                  </Col>
                </Row>

                <Row>
                  <div className='d-grid gap-2 mt-5'>
                    <Button
                      id='btn-edit-data-facturacion'
                      variant='primary'
                      size='lg'
                      onClick={(e) => handleSave(e)}
                      disabled={
                        savingData ||
                        validatingNIP ||
                        validatingPostalCode ||
                        loadingProvincias ||
                        loadingDistritos ||
                        loadingPoblaciones
                      }
                    >
                      {savingData ? (
                        <MiniLoader />
                      ) : (
                        t('datos.facturacion.form.save-btn')
                      )}
                    </Button>
                  </div>
                </Row>
              </Form>
            )}
          </Row>
        </Container>
      </Container>
    </>
  );
};
